import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  useIsFetching,
  useIsMutating,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { parseDecimal } from "@bw/shared/utils";

import {
  updateSettings,
  type PlatformSettingsUpdate,
  type ValidatedPlatformSettings,
} from "../api";
import {
  reconcilePlatformSettings,
  retryPlatformSettingsRead,
  SettingsAuthorityError,
  PLATFORM_SETTINGS_MUTATION_KEY,
  PLATFORM_SETTINGS_QUERY_KEY,
} from "../settingsAuthority";
import {
  useSettingsAuthority,
  type SettingsDesiredMutation,
  type SettingsWriteLease,
} from "../settingsAuthorityStore";
import { settingsUpdateErrorMessage } from "../settingsErrors";

function formatRateInput(rate: number): string {
  const percent = rate * 100;
  return Number.isInteger(percent) ? String(percent) : percent.toFixed(2);
}

function describeUpdateError(error: unknown): string {
  return settingsUpdateErrorMessage(
    error,
    "Failed to update the default platform fee.",
  );
}

const RECONCILIATION_ERROR =
  "The saved platform fee could not be confirmed because settings could not be refreshed. Retry to refresh settings.";
const DIVERGENCE_ERROR =
  "The latest platform fee differs from the requested value. Review it before trying again.";

function ratesMatch(left: number, right: number): boolean {
  return Math.abs(left - right) <= 0.000001;
}

export function CommissionSettingsPanel({
  settings,
}: {
  settings: ValidatedPlatformSettings;
}) {
  const queryClient = useQueryClient();
  const authority = useSettingsAuthority(queryClient);
  useIsFetching({ exact: true, queryKey: PLATFORM_SETTINGS_QUERY_KEY });
  const settingsWriteCount = useIsMutating({
    exact: true,
    mutationKey: PLATFORM_SETTINGS_MUTATION_KEY,
  });
  const initialResolutionSequence = useRef(authority.resolution?.sequence ?? 0);
  const observedSettingsRate = useRef(settings.default_commission_rate);
  const [commissionPercentInput, setCommissionPercentInput] = useState(() =>
    formatRateInput(settings.default_commission_rate),
  );
  const [commissionMessage, setCommissionMessage] = useState<{
    tone: "error";
    message: string;
  } | null>(null);
  const authorityRecovery =
    authority.recovery?.kind === "commission" ? authority.recovery : null;
  const authorityResolution =
    authority.resolution &&
    authority.resolution.sequence > initialResolutionSequence.current &&
    authority.resolution.desired.kind === "commission"
      ? authority.resolution
      : null;
  const settingsReadUncertain =
    queryClient.getQueryState(PLATFORM_SETTINGS_QUERY_KEY)?.status === "error";
  const writesBlocked =
    authority.recovery !== null ||
    authority.refreshing ||
    authority.writing ||
    settingsWriteCount > 0 ||
    settingsReadUncertain;

  const commissionMutation = useMutation({
    mutationKey: PLATFORM_SETTINGS_MUTATION_KEY,
    mutationFn: async ({
      lease,
      payload,
    }: {
      lease: SettingsWriteLease;
      payload: PlatformSettingsUpdate;
    }) => {
      await updateSettings(payload);
      if (!authority.isWriteOperationCurrent(lease)) return null;
      return reconcilePlatformSettings(queryClient, () =>
        authority.isWriteOperationCurrent(lease),
      );
    },
  });

  useEffect(() => {
    if (observedSettingsRate.current === settings.default_commission_rate) {
      return;
    }
    observedSettingsRate.current = settings.default_commission_rate;
    setCommissionPercentInput(
      formatRateInput(settings.default_commission_rate),
    );
    setCommissionMessage(null);
  }, [settings.default_commission_rate]);

  const parsedCommissionPercent = parseDecimal(commissionPercentInput);
  const commissionInputValid =
    parsedCommissionPercent !== null &&
    parsedCommissionPercent >= 0 &&
    parsedCommissionPercent <= 99.99;
  const commissionRate = commissionInputValid
    ? parsedCommissionPercent / 100
    : null;
  const commissionDirty =
    commissionRate !== null &&
    Math.abs(commissionRate - settings.default_commission_rate) > 0.000001;

  async function handleCommissionSave() {
    if (
      commissionMutation.isPending ||
      commissionRate === null ||
      !commissionDirty ||
      queryClient.isMutating({
        exact: true,
        mutationKey: PLATFORM_SETTINGS_MUTATION_KEY,
      }) > 0 ||
      !authority.tryStartWrite()
    ) {
      return;
    }
    const lease = authority.beginWriteOperation();
    if (!lease) {
      authority.finishWrite();
      return;
    }
    setCommissionMessage(null);
    const desired = Object.freeze({
      kind: "commission",
      rate: commissionRate,
    }) satisfies SettingsDesiredMutation;
    const payload = Object.freeze({
      default_commission_rate: commissionRate,
    }) satisfies PlatformSettingsUpdate;
    try {
      const authoritative = await commissionMutation.mutateAsync({
        lease,
        payload,
      });
      if (!authoritative || !authority.isWriteOperationCurrent(lease)) {
        return;
      }
      setCommissionPercentInput(
        formatRateInput(authoritative.default_commission_rate),
      );
      if (ratesMatch(authoritative.default_commission_rate, commissionRate)) {
        authority.recordResolution(desired, "confirmed");
      } else {
        authority.recordResolution(desired, "diverged");
      }
    } catch (error) {
      if (!authority.isWriteOperationCurrent(lease)) return;
      if (error instanceof SettingsAuthorityError) {
        authority.recordRecovery(desired);
      } else {
        setCommissionMessage({
          tone: "error",
          message: describeUpdateError(error),
        });
      }
    } finally {
      authority.finishWrite(lease);
    }
  }

  async function handleAuthorityRetry() {
    if (!authorityRecovery || commissionMutation.isPending) {
      return;
    }
    const recovery = authorityRecovery;
    if (!authority.tryStartRefresh(recovery)) return;
    try {
      const authoritative = await retryPlatformSettingsRead(queryClient);
      setCommissionPercentInput(
        formatRateInput(authoritative.default_commission_rate),
      );
      authority.resolveRecovery(
        recovery,
        ratesMatch(authoritative.default_commission_rate, recovery.rate)
          ? "confirmed"
          : "diverged",
      );
    } catch {
      authority.finishRefresh(recovery);
    }
  }

  return (
    <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
      <Stack spacing={2}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
          Commission defaults
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ alignItems: { xs: "stretch", sm: "flex-start" } }}
        >
          <TextField
            id="default-commission-rate"
            label="Default platform fee"
            type="number"
            value={commissionPercentInput}
            disabled={settingsReadUncertain}
            error={commissionPercentInput.length > 0 && !commissionInputValid}
            onChange={(event) => {
              setCommissionPercentInput(event.target.value);
              setCommissionMessage(null);
            }}
            slotProps={{
              htmlInput: { min: 0, max: 99.99, step: 0.01 },
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              },
            }}
            sx={{ maxWidth: 320 }}
          />
          <Button
            size="small"
            variant="contained"
            disabled={
              !commissionInputValid ||
              !commissionDirty ||
              commissionMutation.isPending ||
              writesBlocked
            }
            onClick={() => void handleCommissionSave()}
            sx={{ mt: { sm: 1 } }}
          >
            {commissionMutation.isPending ? "Saving…" : "Save default fee"}
          </Button>
        </Stack>

        {!commissionInputValid ? (
          <Typography color="error" variant="body2">
            Enter a percentage from 0 to 100 (100 is not allowed).
          </Typography>
        ) : null}

        {authorityRecovery ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                disabled={authority.refreshing}
                onClick={() => void handleAuthorityRetry()}
              >
                {authority.refreshing ? "Retrying…" : "Retry"}
              </Button>
            }
          >
            {RECONCILIATION_ERROR}
          </Alert>
        ) : authorityResolution ? (
          <Alert
            severity={
              authorityResolution.outcome === "confirmed" ? "success" : "error"
            }
          >
            {authorityResolution.outcome === "confirmed"
              ? "Default platform fee updated."
              : DIVERGENCE_ERROR}
          </Alert>
        ) : commissionMessage ? (
          <Alert severity="error">{commissionMessage.message}</Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}
